import { useKeepAwake } from '@/hooks/use-keep-awake';
import { Check } from '@/icons/Check';
import { cn } from '@/utils/cn';
import { STEP_LABELS, type StepType } from '@/utils/persona-import-steps';
import { Fragment, useRef } from 'react';
import {
  ActivityIndicator,
  Platform,
  ScrollView,
  StyleSheet,
  View
} from 'react-native';
import Svg, { Path } from 'react-native-svg';
import { useDeepCompareEffect } from 'use-deep-compare';
import CircularProgress from './circular-progress';
import { Text } from './ui/text';

export type ProgressStep = {
  stepType: StepType | 'uploading';
  completed?: boolean;
};

// don't change this without also changing the height of the ScrollView
const STEP_HEIGHT = 50;

export function ProgressScreen({
  progress,
  completed,
  completedText,
  progressText,
  progressSteps
}: {
  progress: number;
  completed?: boolean;
  completedText?: string;
  progressText?: string;
  progressSteps: ProgressStep[];
}) {
  const ref = useRef<ScrollView>(null);

  useDeepCompareEffect(() => {
    if (ref.current) {
      const firstUncompletedStep =
        progressSteps.findIndex((step) => !step.completed) - 1;
      if (firstUncompletedStep > -1) {
        ref.current.scrollTo({
          y: firstUncompletedStep * STEP_HEIGHT,
          animated: true
        });
      }
    }
  }, [progressSteps]);

  return (
    <View className="mt-4 w-full items-center">
      <CircularProgress
        size={300}
        width={30}
        fill={progress}
        arcSweepAngle={240}
        rotation={240}
        tintColor="#8FF3E7"
        dashedBackground={{ width: 3, gap: 4 }}
        dashedTint={{ width: 3, gap: 4 }}
        backgroundColor="#3d5875"
      >
        <Svg width={91} height={102} viewBox="0 0 91 102" fill="none">
          <Path
            d="M45.44025 0.220154L46.06685 7.4153C48.12245 31.0187 67.085 49.59585 90.7255 51.1665C67.085 52.7365 48.1224 71.314 46.06685 94.9175L45.44025 102L44.8136 94.9175C42.75805 71.314 23.79545 52.7365 0.154785 51.1665C23.7954 49.59585 42.75805 31.0187 44.8136 7.4153L45.44025 0.220154Z"
            fill="white"
          />
        </Svg>
      </CircularProgress>

      <Text className="-mt-4 text-4xl">
        {completed ? '100%' : `${Math.round(progress)}%`}
      </Text>
      {completed && completedText ? (
        <Text className="mt-2">{completedText}</Text>
      ) : progressText ? (
        <Text className="mt-2">{progressText}</Text>
      ) : null}

      {!completed && (
        <Fragment>
          <KeepAwake />

          <ScrollView
            ref={ref}
            className="mt-4 h-24 w-full divide-y divide-border rounded-lg border border-border bg-card px-5"
          >
            {progressSteps.map((step, index) => {
              const previousStep = progressSteps[index - 1];
              const currentStep = progressSteps.find((step) => !step.completed);
              return (
                <View
                  key={step.stepType}
                  className="flex h-12 flex-row items-center justify-between"
                >
                  <Text
                    className={cn(
                      'pr-2',
                      step.stepType === currentStep?.stepType
                        ? 'text-white'
                        : 'text-gray-500'
                    )}
                  >
                    {step.stepType === 'uploading'
                      ? 'Uploading…'
                      : STEP_LABELS[step.stepType]}
                  </Text>
                  {step.completed ? (
                    <Check color="white" />
                  ) : !previousStep || previousStep?.completed ? (
                    <ActivityIndicator size="small" style={styles.spinner} />
                  ) : null}
                </View>
              );
            })}
          </ScrollView>
        </Fragment>
      )}
    </View>
  );
}

function KeepAwake() {
  useKeepAwake();

  return null;
}

const styles = StyleSheet.create({
  spinner: {
    marginRight: Platform.OS === 'android' ? 10 : 0
  }
});
